import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  TextField,
  InputAdornment,
  ButtonBase,
} from '@mui/material';
import {
  Help,
  Close,
  ExpandMore,
  Search,
  HelpOutline,
  DescriptionOutlined,
} from '@mui/icons-material';
import { useState, useEffect, useMemo } from 'react';
import { getTutoriais, downloadTutorial } from '../configs/functions';
import { useCommonItems } from '../../../../../../contexts/CommonItensProvider';

const GREEN = '#275740';

const FILTROS = [
  { value: 'todos', label: 'Todos' },
  { value: 'duvida', label: 'Perguntas' },
  { value: 'tutorial', label: 'Tutoriais' },
];

function FilterChip({ label, count, selected, onClick }) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        px: 1.75,
        py: 0.75,
        borderRadius: 99,
        fontSize: 13,
        fontWeight: 500,
        border: '1px solid',
        borderColor: selected ? GREEN : '#e0e0e0',
        backgroundColor: selected ? GREEN : '#fff',
        color: selected ? '#fff' : '#424242',
        transition: 'all 0.2s',
      }}
    >
      {label}
      <Box component="span" sx={{ ml: 0.75, opacity: selected ? 0.85 : 0.6 }}>
        {count}
      </Box>
    </ButtonBase>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography
      variant="caption"
      sx={{
        fontWeight: 700,
        letterSpacing: 1,
        color: 'text.secondary',
        textTransform: 'uppercase',
        mt: 1,
      }}
    >
      {children}
    </Typography>
  );
}

function ItemAccordion({ icon, iconBg, iconColor, titulo, subtitulo, children }) {
  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        border: '1px solid #ececec',
        borderRadius: '12px !important',
        backgroundColor: '#fff',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: iconBg,
              color: iconColor,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {titulo}
            </Typography>
            {subtitulo && (
              <Typography variant="caption" color="text.secondary">
                {subtitulo}
              </Typography>
            )}
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0, pl: 7.5 }}>{children}</AccordionDetails>
    </Accordion>
  );
}

export function TutorialMenu({ open, onClose, onSuporte }) {
  const [itens, setItens] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [busca, setBusca] = useState('');
  const [filtro, setFiltro] = useState('todos');
  const { exibirAlerta } = useCommonItems();

  async function carregaTutoriais() {
    setIsLoading(true);
    try {
      const res = await getTutoriais();
      const dados = res?.data?.data ?? [];
      setItens(dados.filter((t) => t.tipoArquivo === 'externo' && t.status === 1));
    } catch (error) {
      exibirAlerta('Erro', 'Erro ao carregar tutoriais', 'error');
      setItens([]);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (open) {
      carregaTutoriais();
    } else {
      setBusca('');
      setFiltro('todos');
    }
  }, [open]);

  const contagem = useMemo(
    () => ({
      todos: itens.length,
      duvida: itens.filter((i) => i.tipo === 'duvida').length,
      tutorial: itens.filter((i) => i.tipo === 'tutorial').length,
    }),
    [itens]
  );

  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return itens.filter((i) => {
      if (filtro !== 'todos' && i.tipo !== filtro) return false;
      if (!termo) return true;
      return (
        i.nome?.toLowerCase().includes(termo) ||
        i.descricao?.toLowerCase().includes(termo)
      );
    });
  }, [itens, busca, filtro]);

  const perguntas = filtrados.filter((i) => i.tipo === 'duvida');
  const tutoriais = filtrados.filter((i) => i.tipo === 'tutorial');

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 450 },
          backgroundColor: '#fafafa',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          backgroundColor: GREEN,
          color: '#fff',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Help />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Ajuda
          </Typography>
        </Box>
        <IconButton aria-label="Fechar" color="inherit" onClick={onClose} size="small">
          <Close />
        </IconButton>
      </Box>

      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1, overflowY: 'auto' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Buscar dúvida ou tutorial"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
            sx: { backgroundColor: '#fff', borderRadius: 2 },
          }}
        />

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {FILTROS.map((f) => (
            <FilterChip
              key={f.value}
              label={f.label}
              count={contagem[f.value]}
              selected={filtro === f.value}
              onClick={() => setFiltro(f.value)}
            />
          ))}
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress color="success" />
          </Box>
        ) : filtrados.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            Nenhum resultado encontrado.
          </Typography>
        ) : (
          <>
            {perguntas.length > 0 && (
              <>
                <SectionTitle>Perguntas frequentes</SectionTitle>
                {perguntas.map((p) => (
                  <ItemAccordion
                    key={p.id}
                    icon={<HelpOutline fontSize="small" />}
                    iconBg="#e3efe9"
                    iconColor={GREEN}
                    titulo={p.nome}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {p.descricao}
                    </Typography>
                  </ItemAccordion>
                ))}
              </>
            )}

            {tutoriais.length > 0 && (
              <>
                <SectionTitle>Tutoriais</SectionTitle>
                {tutoriais.map((t) => (
                  <ItemAccordion
                    key={t.id}
                    icon={<DescriptionOutlined fontSize="small" />}
                    iconBg="#fdeee3"
                    iconColor="#e6783c"
                    titulo={t.nome}
                  >
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {t.descricao ?? 'Clique no botão abaixo para conferir o tutorial'}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: GREEN,
                        cursor: 'pointer',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                      onClick={() => downloadTutorial(t.id)}
                    >
                      Clique aqui!
                    </Typography>
                  </ItemAccordion>
                ))}
              </>
            )}
          </>
        )}
      </Box>

    </Drawer>
  );
}